import { CohortSummaryAttributeResult } from '@verantos-dev/types-cohorts';

const getGenderAttributeLabelForKey = (key: string) => {
  switch (key) {
    case 'MALE':
      return 'Male';
    case 'FEMALE':
      return 'Female';
    case 'OTHER':
      return 'Other';
    case 'UNKNOWN':
      return 'Unknown';
    default:
      return key;
  }
};

export const sanitizeAgeAtIndexData = (data: CohortSummaryAttributeResult[] | undefined) => {
  if (!data || data.length === 0) return;

  return data.map((item) => {
    const [start, end] = item.attributeName.split('-');
    return {
      ...item,
      attributeName: `${start} - ${end}`,
    };
  });
};

export const sanitizeGenderData = (data: CohortSummaryAttributeResult[] | undefined) => {
  if (!data || data.length === 0) return;

  return data.map((item) => {
    return {
      ...item,
      attributeName: getGenderAttributeLabelForKey(item.attributeName),
    };
  });
};

// generating range for xAxis labels of cohort characteristics age chart
export const generateRangeArray = (start: number, end: number, interval: number): string[] => {
  const arr: string[] = [];
  for (let i = start; i < end; i += interval) {
    arr.push(`${i} - ${i + interval - 1}`);
  }
  return arr;
};

/**
 * Calculates the axis data for a given range of steps and cohort summary attribute results.
 *
 * @param {string[]} rangeSteps - An array of strings representing the range steps.
 * @param {CohortSummaryAttributeResult[]} data - An array of cohort summary attribute results.
 * @returns {Array<{ attributeName: string, attributeCount: number }>} An array of objects containing the attribute name and attribute count for each step in the range.
 */
export const calculateAxisData = (rangeSteps: string[], data: CohortSummaryAttributeResult[]) => {
  const barStepsValue = rangeSteps.map((step) => {
    const row = data.filter((item) => item.attributeName === step);
    return { ...row[0], attributeName: step, attributeCount: row[0]?.attributeCount || 0 };
  });

  return barStepsValue;
};

export const calculateChartDimensions = (windowWidth: number) => {
  const width = windowWidth > 1920 ? 1920 : windowWidth; // Limiting to be semi-responsive

  const aspectRatio = 2; // Width:Height ratio of 2:1
  const minWidth = 345;
  const minHeight = 200;
  const minInnerRadius = 35;
  const scaleStartWidth = 1350; // The threshold width to start scaling up

  let dynamicWidth = minWidth;
  let dynamicHeight = minHeight;
  let dynamicInnerRadius = minInnerRadius;

  // Only scale if the screen width exceeds 1350px
  if (width > scaleStartWidth) {
    const scaleFactor = Math.log2(width - scaleStartWidth + 1) / 50; // Smooth logarithmic scaling
    dynamicWidth = minWidth + (width - scaleStartWidth) * scaleFactor; // Increase width from the minimum
    dynamicHeight = dynamicWidth / aspectRatio; // Maintain aspect ratio

    // Start with logarithmic scaling, but transition to linear scaling for a smoother growth
    const linearInnerRadiusFactor = 0.1; // A small linear factor for smoother growth
    dynamicInnerRadius = minInnerRadius + (dynamicWidth - minWidth) * linearInnerRadiusFactor;
  }

  return {
    width: dynamicWidth,
    height: dynamicHeight,
    innerRadius: dynamicInnerRadius, // Adjusting inner radius to scale with chart size
  };
};

export const wrapLabel = (label: string, maxCharCount: number) => {
  const words = label.split(' ');
  let currentLine = '';
  let formattedLabel = '';
  let longestLine = 0;

  words.forEach((word) => {
    if ((currentLine + word).length <= maxCharCount) {
      currentLine += word + ' ';
    } else {
      // Add the current line to the formatted label and start a new line
      formattedLabel += currentLine.trim() + '\n';
      longestLine = Math.max(longestLine, currentLine.trim().length);
      currentLine = word + ' ';
    }
  });

  // Append any remaining words in the last line
  return (formattedLabel += currentLine.trim());
};
